import { useCallback, useMemo, useState } from 'react';

import Box from '@mui/material/Box';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';

import { getFragmentData } from '@e-socna/data-layer';
import type { FragmentType } from '@e-socna/data-layer';

import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { orderedActorsOf } from '../Actors/utils/actors-order.util';

import { ActorListItem } from './ActorListItem';
import { FolderBusinessEntityListCmp_ThirdParty_Fragment } from './fragments/third-party.fragment';

interface CarouselActor {
  readonly thirdPartyId: string;
  readonly categoryIds: readonly string[];
  readonly thirdParty: FragmentType<
    typeof FolderBusinessEntityListCmp_ThirdParty_Fragment
  >;
}

interface Props {
  readonly actors: readonly CarouselActor[];
}

const previousLabel = 'Acteur externe précédent';
const nextLabel = 'Acteur externe suivant';
const DOT_SIZE_PX = 8;

// The external actors of an entity one at a time, the quotation receivers first, then
// alphabetically (as the actors consultation): dots to pick one, arrows to step, no autoplay.
export const ExternalActorsCarousel = ({ actors }: Readonly<Props>) => {
  const { actorCategoriesCodeMap } = useReferencesContext();
  const [index, setIndex] = useState(0);

  const orderedActors = useMemo(
    () =>
      orderedActorsOf(
        actors.map((actor) => ({
          actor,
          categoryIds: actor.categoryIds,
          thirdParty: getFragmentData(
            FolderBusinessEntityListCmp_ThirdParty_Fragment,
            actor.thirdParty,
          ),
        })),
        actorCategoriesCodeMap.QuotationReceiver._id,
      ).map(({ actor }) => actor),
    [actorCategoriesCodeMap.QuotationReceiver._id, actors],
  );

  const showPrevious = useCallback(() => {
    setIndex((current) => Math.max(current - 1, 0));
  }, []);
  const showNext = useCallback(() => {
    setIndex((current) => Math.min(current + 1, orderedActors.length - 1));
  }, [orderedActors.length]);

  const shownIndex = Math.min(index, orderedActors.length - 1);
  const shownActor = orderedActors[shownIndex];

  if (!shownActor) {
    return null;
  }

  return (
    <Box>
      <List sx={{ width: '100%' }}>
        <ActorListItem actor={shownActor} />
      </List>
      {orderedActors.length > 1 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          <IconButton
            aria-label={previousLabel}
            disabled={!shownIndex}
            onClick={showPrevious}
            size="small"
          >
            <ChevronLeftIcon />
          </IconButton>
          {orderedActors.map((actor, actorIndex) => (
            <Box
              aria-current={actorIndex === shownIndex}
              aria-label={`Acteur externe ${(actorIndex + 1).toFixed()}`}
              component="button"
              key={actor.thirdPartyId}
              // eslint-disable-next-line react/jsx-no-bind
              onClick={() => {
                setIndex(actorIndex);
              }}
              sx={{
                width: DOT_SIZE_PX,
                height: DOT_SIZE_PX,
                p: 0,
                border: 'none',
                borderRadius: '50%',
                cursor: 'pointer',
                bgcolor:
                  actorIndex === shownIndex
                    ? 'primary.main'
                    : 'action.disabled',
              }}
              type="button"
            />
          ))}
          <IconButton
            aria-label={nextLabel}
            disabled={shownIndex === orderedActors.length - 1}
            onClick={showNext}
            size="small"
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};
